import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  gql,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';

/**
 * website-api only treats operationName === "RefreshToken" as a refresh:
 * context.me becomes the refresh cookie / x-refresh-token string.
 */
export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken {
    refreshToken {
      token
      user {
        id
        email
        role
        mfaEnabled
      }
    }
  }
`;

const SKIP_REFRESH_OPS = new Set([
  'RefreshToken',
  'AdminLogin',
  'AdminCompleteMfa',
]);

function isUnauthenticated(graphQLErrors) {
  return (graphQLErrors || []).some(
    (err) =>
      err?.extensions?.code === 'UNAUTHENTICATED'
      || /session expired|invalid token|token has expired|must be authenticated/i.test(
        err?.message || ''
      )
  );
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  const next = `${window.location.pathname}${window.location.search || ''}`;
  const params = new URLSearchParams({ reason: 'session_expired' });
  if (next && next !== '/') params.set('next', next);
  window.location.assign(`/login?${params.toString()}`);
}

/**
 * Cookie-session Apollo client for Admin Console.
 * On UNAUTHENTICATED, runs RefreshToken (credentials include) once, then retries.
 */
export function createAdminApolloClient({
  uri = process.env.REACT_APP_API_URL || 'http://localhost:4000/graphql',
} = {}) {
  let client;
  let refreshPromise = null;

  const httpLink = createHttpLink({
    uri,
    credentials: 'include',
  });

  const errorLink = onError(({ graphQLErrors, operation, forward }) => {
    if (SKIP_REFRESH_OPS.has(operation.operationName)) {
      if (
        operation.operationName === 'RefreshToken'
        && isUnauthenticated(graphQLErrors)
      ) {
        redirectToLogin();
      }
      return undefined;
    }

    if (!isUnauthenticated(graphQLErrors)) {
      return undefined;
    }

    if (!refreshPromise) {
      refreshPromise = client
        .mutate({ mutation: REFRESH_TOKEN_MUTATION })
        .then((result) => {
          if (!result?.data?.refreshToken?.token) {
            throw new Error('RefreshToken returned no access token');
          }
          return true;
        })
        .catch((err) => {
          redirectToLogin();
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    return new Observable((observer) => {
      let sub;
      refreshPromise
        .then(() => {
          sub = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch((err) => {
          observer.error(err);
        });
      return () => {
        if (sub) sub.unsubscribe();
      };
    });
  });

  client = new ApolloClient({
    link: from([errorLink, httpLink]),
    cache: new InMemoryCache(),
    connectToDevTools: process.env.NODE_ENV !== 'production',
  });

  return client;
}

const apolloClient = createAdminApolloClient();
export default apolloClient;
